import { CARD_API_ENDPOINT } from '@/config/consts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { normalizeCardLimitTier } from '../utils/limitCaps'
import { createSmartFetch, createSmartGet } from './useAuthenticatedMutation'

export type RainFrequency =
  | 'per24HourPeriod'
  | 'per7DayPeriod'
  | 'per30DayPeriod'
  | 'perYearPeriod'
  | 'allTime'
  | 'perAuthorization'

export const LIMIT_FREQUENCY_OPTIONS: { value: RainFrequency; label: string }[] = [
  { value: 'per24HourPeriod', label: 'Daily' },
  { value: 'per7DayPeriod', label: 'Weekly' },
  { value: 'per30DayPeriod', label: 'Monthly' },
  { value: 'allTime', label: 'Lifetime' },
  { value: 'perAuthorization', label: 'Per Charge' },
]

const KNOWN_FREQUENCIES = new Set<string>(LIMIT_FREQUENCY_OPTIONS.map((o) => o.value))

function normalizeFrequency(raw: string): RainFrequency {
  if (KNOWN_FREQUENCIES.has(raw)) return raw as RainFrequency
  return 'per24HourPeriod'
}

interface BorrowLimitResponse {
  data: {
    amount: number
    frequency: string
    currency: string
    tier?: unknown
  }
}

interface UpdateBorrowLimitPayload {
  limit: number
  frequency: RainFrequency
  cardID: string
}

export function useBorrowLimit(cardID: string) {
  const queryClient = useQueryClient()

  const limitUrl = `${CARD_API_ENDPOINT}/v1/vendor/cards/limit/${cardID}`
  const { data, isLoading, error } = useQuery<BorrowLimitResponse>({
    queryKey: ['borrowLimit', cardID],
    queryFn: async () => {
      const getBorrowLimit = createSmartGet(limitUrl)
      return getBorrowLimit()
    },
    enabled: !!cardID,
  })

  const updateUrl = `${CARD_API_ENDPOINT}/v1/vendor/cards/limits`
  const updateMutation = useMutation<BorrowLimitResponse, Error, UpdateBorrowLimitPayload>({
    mutationFn: async (payload: UpdateBorrowLimitPayload) => {
      const updateBorrowLimit = createSmartFetch(updateUrl, { method: 'PATCH' })
      return updateBorrowLimit(payload)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['borrowLimit', variables.cardID] })
    },
  })

  return {
    data: data?.data
      ? (() => {
          const tier = normalizeCardLimitTier(data.data.tier)
          return {
            ...data.data,
            tier,
            limit: (data.data.amount / 100).toFixed(2),
            frequency: normalizeFrequency(data.data.frequency),
          }
        })()
      : undefined,
    isLoading,
    error,
    updateLimit: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
  }
}
